import type { NavigationLayout, NavigationMode } from 'common/environmentConfiguration';
import { getEnvironmentNavigation } from 'common/environmentConfiguration';
import type { ConfigurationItem } from 'common/models';
import { useEnvironmentConfiguration } from 'common/useEnvironmentConfiguration';
import Alert from 'components/Alert/Alert';
import Button from 'components/Button/Button';
import { useIsLeftMenuSidebarEnabled } from 'helpers/featureFlagsHelper';
import { useCustomTranslation } from 'helpers/translationHelper';
import type { PropsWithChildren } from 'react';
import { createContext, useContext, useMemo } from 'react';

export type { NavigationLayout } from 'common/environmentConfiguration';
const EMPTY_CONFIGURATION: ConfigurationItem[] = [];

const NavigationLayoutContext = createContext<
  | {
      layout: NavigationLayout;
      mode: NavigationMode;
      canSwitchLayout: boolean;
      setLayout: (layout: NavigationLayout) => void;
      setMode: (mode: NavigationMode) => void;
    }
  | undefined
>(undefined);

/** Shared selection keeps both user menus and the shell in sync without reloading the page. */
const NavigationLayoutProvider = ({
  children,
  isSuperAdmin = false,
  initialConfiguration = EMPTY_CONFIGURATION
}: PropsWithChildren<{ isSuperAdmin?: boolean; initialConfiguration?: ConfigurationItem[] }>) => {
  const { configuration, updateNavigation, saveFailed, retrySave } =
    useEnvironmentConfiguration(initialConfiguration);
  const { tCommon } = useCustomTranslation();
  const sidebarEnabled = useIsLeftMenuSidebarEnabled();
  const canSwitchLayout = isSuperAdmin || sidebarEnabled;
  const { layout, mode } = getEnvironmentNavigation(configuration, isSuperAdmin);
  // Superadmins always have access; retain other users' preference while the flag is off.
  const effectiveLayout = canSwitchLayout ? layout : 'top';
  const value = useMemo(
    () => ({
      layout: effectiveLayout,
      mode,
      canSwitchLayout,
      setLayout: (next: NavigationLayout) => {
        if (canSwitchLayout) updateNavigation({ layout: next });
      },
      setMode: (next: NavigationMode) => updateNavigation({ mode: next })
    }),
    [effectiveLayout, mode, canSwitchLayout, updateNavigation]
  );
  return (
    <NavigationLayoutContext.Provider value={value}>
      {saveFailed && (
        <Alert
          type="error"
          message={tCommon('environment_save_failed', {
            defaultValue:
              'Nastavení prostředí se nepodařilo uložit. Vaše volba zůstává platná do obnovení stránky.'
          })}
          action={<Button onClick={retrySave}>{tCommon('retry')}</Button>}
        />
      )}
      {children}
    </NavigationLayoutContext.Provider>
  );
};

export const useNavigationLayout = () => {
  const value = useContext(NavigationLayoutContext);
  if (!value) throw new Error('useNavigationLayout requires NavigationLayoutProvider');
  return value;
};

export default NavigationLayoutProvider;
